import { memo } from "react";

import { type SlideComponent, type Splide } from "@splidejs/splide";
import { Intersection } from "@splidejs/splide-extension-intersection";

import { Text } from "@components/Text";
import {
  Carousel,
  Track,
  CarouselArrow,
  CarouselArrows,
} from "@react/components/Carousel";
import { PAGE_CAROUSEL_OPTIONS } from "@react/components/Carousel/constants";
import { type TTailwindString, tw } from "classnames";

import { UGCVideoCarouselSlide } from "./UGCVideoCarouselSlide.data";

const ugcCarouselOptions = {
  ...PAGE_CAROUSEL_OPTIONS,
  gap: 12,
  autoplay: "pause" as const,
  interval: 5000,
  rewind: true,
  intersection: {
    inView: {
      autoplay: true,
    },
    outView: {
      autoplay: false,
    },
  },
};

type Props = {
  carouselAria?: {
    "aria-describedby"?: string;
    "aria-label"?: string;
    "aria-roledescription": string;
  };
  heading: string;
  pauseVideo: (splide: Splide, slide: SlideComponent) => void;
  playVideo: (splide: Splide, slide: SlideComponent) => void;
  text: {
    playVideoModal: string;
  };
  videos: {
    posterImage?: string;
    sources: { mimeType: string; url: string }[];
    subtitles?: string;
    title: string;
  }[];
};

const styles = {
  base: tw(
    "border-0",
    "border-t",
    "border-b",
    "border-solid",
    "border-divider",
    "py-6"
  ),
  headingContainer: tw(
    "md:h-10",
    "md:flex",
    "md:items-center",
    "mb-6",
    "text-primary"
  ),
  slide: tw("transition-opacity"),
  modalButton:
    "relative block h-[180px] w-[110px] overflow-hidden rounded-lg md:h-[195px] md:w-30",
  videoTitle: tw(
    "absolute",
    "bottom-1.5",
    "left-1.5",
    "text-left",
    "text-white",
    "md:bottom-[7px]" as TTailwindString,
    "md:left-[7px]" as TTailwindString
  ),
  arrows: tw(
    "hidden",
    "md:inline-flex",
    "absolute",
    "-top-16",
    "right-0",
    "gap-5"
  ),
};

const UGCVideoCarouselViewBase = ({
  carouselAria,
  heading,
  pauseVideo,
  playVideo,
  text,
  videos,
}: Props) => {
  return (
    <div className={styles.base}>
      <div className={styles.headingContainer}>
        <Text as="title2">{heading}</Text>
      </div>
      <Carousel
        extensions={{ Intersection }}
        hasTrack={false}
        onActive={playVideo}
        onInactive={pauseVideo}
        options={ugcCarouselOptions}
        {...(carouselAria
          ? {
              "aria-label": carouselAria["aria-label"],
              "aria-roledescription": carouselAria["aria-roledescription"],
              "aria-describedby": carouselAria["aria-describedby"],
            }
          : {})}
      >
        <Track>
          {videos.map((video) => (
            <UGCVideoCarouselSlide
              key={video.title}
              text={text}
              video={video}
            />
          ))}
        </Track>
        <CarouselArrows className={styles.arrows}>
          <CarouselArrow direction="prev" size="sm" />
          <CarouselArrow direction="next" size="sm" />
        </CarouselArrows>
      </Carousel>
    </div>
  );
};

export const UGCVideoCarouselView = memo(UGCVideoCarouselViewBase);
